import * as Sentry from '@sentry/react';
import {createBrowserRouter} from 'react-router';
import {HELP_URL_LOCALES} from '../help-center/locales';
import AppContainer from './AppContainer';
import RouteErrorBoundary from './RouteErrorBoundary';
import {authRoutes} from './auth/routes';
import {dashboardRoutes} from './dashboard/routes';
import {helpContentRoutes} from './help/routes';

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);

const router = sentryCreateBrowserRouter([
  {
    path: '/',
    element: <AppContainer />,
    // Catches render/loader errors from any descendant route and forwards them
    // to Sentry (the default React Router boundary swallows them silently).
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: '',
        lazy: () => import('../screens/LandingPage'),
      },
      {
        path: 'auth',
        children: authRoutes,
      },
      {
        // The unified map host (#327), for everyone, signed in or out:
        // observations and forecast (GFS free, other models Premium).
        path: 'map',
        lazy: () => import('../screens/Map'),
      },
      {
        // Top-level (like /map) so it renders full-width without the dashboard
        // nav rail. Everyone, no flag: the flipbook ("Open as animation")
        // and the redirects into /map's forecast mode.
        path: 'forecast-maps',
        lazy: () => import('../screens/ForecastMaps'),
      },
      {
        path: 'forecast-maps/builder',
        lazy: () => import('../screens/ForecastMaps/Builder'),
      },
      {
        // Flag-gated (forecast_map_browse), as is /models: the screen
        // redirects to /map's forecast mode when off.
        path: 'forecast-maps/zones',
        lazy: () => import('../screens/ForecastMaps/Zones'),
      },
      {
        path: 'forecast-maps/models',
        lazy: () => import('../screens/ForecastMaps/Models'),
      },
      {
        path: 'app',
        lazy: () => import('./dashboard'),
        children: dashboardRoutes,
      },
      {
        path: 'terms-and-conditions',
        lazy: () => import('../screens/legal/TermsOfService'),
      },
      {
        path: 'privacy-policy',
        lazy: () => import('../screens/legal/PrivacyPolicy'),
      },
      {
        path: 'account-deletion',
        lazy: () => import('../screens/legal/AccountDeletionInformation'),
      },
      {
        path: 'help',
        lazy: () => import('../screens/HelpCenter'),
        children: [
          // Locale-prefixed help URLs (#70): static locale segments declared
          // before `:slug` so `/help/fr` can never be read as an article (no
          // article slug collides with a locale code). English stays at the
          // bare, historically-indexed URLs.
          ...HELP_URL_LOCALES.map((locale) => ({
            path: locale,
            children: helpContentRoutes(),
          })),
          ...helpContentRoutes(),
        ],
      },
      {
        path: '*',
        lazy: async () => import('../screens/NotFound'),
      },
    ],
  },
]);

export default router;
